import {
	DynamicContext,
	DynamicUserProfile,
	useDynamicContext,
	useIsLoggedIn,
	useDynamicEvents,
	UserProfile,
} from '@dynamic-labs/sdk-react-core'
import { useEffect, useState } from 'react'
import TypographyDisplay from '#app/components/typography/TypographyDisplay'
import Button from '../buttons/button'
import Toast from '../toastsAndMessages/toast'
import { toast } from 'sonner'
import {
	CompletedIcon,
	CaretDown,
	LoadingIcon,
	OpenSeaIcon,
	PlusIcon,
	SignIcon,
	WalletIcon,
	XIcon,
} from '../icons'
import CertifyIcon from '../icons/Miscellaneous/CertifyIcon'
import {
	capitalizeFirstLetter,
	getCollectionBlockchainNamesString,
	getCollectionPreviewURL,
	getShareAttestationToXURL,
	isValidEthereumAddress,
	lessTruncateStringInMiddle,
	mapReferenceEntriesToFormData,
	truncateStringInMiddle,
} from '#app/utils/misc'
import TypographyHeading from '../typography/TypographyHeading'
import TypographyParagraph from '../typography/TypographyParagraph'
import TypographyLabel from '../typography/TypographyLabel'
import CollectionForm from '../common/signForms/collectionForm'
import { Timeline } from '../timeline'
import CollectionTemplate from '#app/utils/templates/collection'
import TypographyCaps from '../typography/TypographyCaps'
import Callout from '../callout/callout'
import useAnalytics from '#app/utils/analytics/useAnalytics'
import {
	generateCollectionMessageToSign,
	mapPinData,
	handlePin,
	type AttestationState,
	getCollectionEntryCounts,
	getLoadingCollectionEntryCounts,
} from '#app/utils/mintUtils'
import { Theme, useTheme } from '#app/utils/providers/themeProvider'
import ContentTile from '../content/ContentTile'
import clsx from 'clsx'
import {
	CollectionNFTInputType,
	CollectionType,
	CollectionNFTInputTypeNameMap,
} from '#app/utils/templates/utils/templateTypes'
import { Link, useNavigate } from '@remix-run/react'
import BackButton from '../common/backButton'
import {
	CollectionInputDataTypes,
	CollectionInputModal,
} from '../inputs/CollectionInputs/collectionInputModal'
import Skeleton, { SkeletonTheme } from 'react-loading-skeleton'
import FallbackAvatar from '#images/avatar11white.png'
import CollectionCard from '../content/CollectionCard'
import { Loader } from '../loaders'
import InlineURLInputField from '../inputs/inlineUrlInputField'
import slugify from 'slugify'
import { Modal } from '../modalsAndOverlays'
import { UserType } from '../inputs/userAccountLookupInput'
import { version } from '#package.json'
import CollectionTemplateType from '#app/utils/templates/collection'
import InternalLink from '../common/internalLink'
import {
	AttestationType,
	PinnedMetadataProperties,
	MetadataProperties,
} from '#types/loreTypes.tsx'

const contractCache: { [contract: string]: number } = {}
const nonEditStates = [
	'pinning',
	'pin error',
	'pinned',
	'signing',
	'sign error',
	'signed',
] as AttestationState[]

export type CollectionResponse = {
	message?: string
	success: boolean
	collection?: {
		name: string
		description: string
		image_url?: string
		banner_image_url?: string
		external_url?: string
		distinct_nft_count: number
		chains: string[]
		top_contracts: string[]
		deployment_date?: string
	}
	inputArray?: CollectionInputDataTypes[]
}

export default function CollectionAddComponent({
	users,
	setConfirmationModal,
	setSignedEntry,
	pendingSign,
	atomicLoreUrl,
}: {
	users: UserType[]
	setConfirmationModal?: (
		confirmationModal: {
			title: string
			message: string
			acceptButton: string
			cancelButton: string | null
		} | null,
	) => void
	setSignedEntry?: (signedEntry: AttestationType) => void
	pendingSign?: PinnedMetadataProperties
	atomicLoreUrl?: string
}) {
	const [theme] = useTheme()
	const {
		primaryWallet,
		user,
		showAuthFlow,
		setShowAuthFlow,
		setShowDynamicUserProfile,
		showDynamicUserProfile,
	} = useDynamicContext()
	const navigate = useNavigate()
	const { trackEvent } = useAnalytics()
	const isAuthenticated = useIsLoggedIn()
	const [selectedArtist, setSelectedArtist] = useState<UserType | undefined>(
		undefined,
	)

	useDynamicEvents('primaryWalletChanged', async newPrimaryWallet => {
		console.log('PRIMARY WALLET CHANGED', newPrimaryWallet)
		setShowDynamicUserProfile(false)
	})

	interface PreviewPinData extends PinnedMetadataProperties {
		userAvatar: string
		username: string
	}

	// Auth State
	const [performingLogin, setPerformingLogin] = useState(false)

	// Attestation State
	const [nftsCount, setNftsCount] = useState(0)
	const [isPinned, setIsPinned] = useState(false)
	const [isSigned, setIsSigned] = useState(false)
	const [attestationState, setAttestationState] =
		useState<AttestationState>('editing')
	const [signingMessage, setSigningMessage] = useState('')
	const [signature, setSignature] = useState('')
	const [pinData, setPinData] = useState<PinnedMetadataProperties | null>(null)
	const [signData, setSignData] = useState<AttestationType | null>(null)
	const [previewPinData, setPreviewPinData] = useState<
		PreviewPinData | undefined
	>(undefined)
	const [attestationUid, setAttestationUid] = useState<string | undefined>()

	// Draft state
	const [showPreSaveDraftModal, setShowPreSaveDraftModal] = useState(false)
	const [showPostSaveDraftModal, setShowPostSaveDraftModal] = useState(false)

	// Data state
	const [formData, setFormData] = useState<MetadataProperties | {}>({})
	const [missingRequiredFields, setMissingRequiredFields] = useState([])
	const [errorMessage, setErrorMessage] = useState('')
	const [errorFields, setErrorFields] = useState({})
	const [collectionNames, setCollectionNames] = useState<string[]>([])
	const [collectionSlugs, setCollectionSlugs] = useState<string[]>([])
	const [isImageUploading, setIsImageUploading] = useState(false)

	const getUserName = ({
		user,
		selectedArtist,
		formData,
		pinData,
		signData,
	}: {
		user: UserProfile | undefined
		selectedArtist: UserType | undefined
		formData?: any
		pinData?: PinnedMetadataProperties
		signData?: AttestationType
	}) => {
		if (selectedArtist) {
			return selectedArtist.username || ''
		}
		if (
			formData &&
			formData.hasOwnProperty('artistName') &&
			formData.artistName
		) {
			return formData.artistName
		}
		if (user && user.username) {
			return user.username
		}
		return ''
	}
	const getUserAvatar = ({
		user,
		selectedArtist,
		formData,
		pinData,
		signData,
	}: {
		user: UserProfile | undefined
		selectedArtist: UserType | undefined
		formData?: any
		pinData?: PinnedMetadataProperties
		signData?: AttestationType
	}) => {
		if (selectedArtist) {
			return selectedArtist.avatar || FallbackAvatar
		}
		if (
			formData &&
			formData.hasOwnProperty('artistAvatar') &&
			formData.artistAvatar
		) {
			return formData.artistAvatar
		}
		if (user && user.metadata && user.metadata.avatar) {
			return user.metadata.avatar
		}
		return FallbackAvatar
	}

	useEffect(() => {
		const getCollectionNames = async () => {
			try {
				const options = {
					method: 'GET',
					headers: { 'Content-Type': 'application/json' },
				}
				const response = await fetch(`/getCollectionNamesAPI`, options)
				const responseJson = (await response.json()) as {
					collectionNamesAndSlugs: { name: string; slug: string }[]
				}
				if (responseJson && responseJson.collectionNamesAndSlugs) {
					const collectionNameResponse =
						responseJson.collectionNamesAndSlugs.map(entry => entry.name)
					const collectionSlugResponse =
						responseJson.collectionNamesAndSlugs.map(entry => entry.slug)
					setCollectionNames(collectionNameResponse)
					setCollectionSlugs(collectionSlugResponse)
				}
			} catch (e) {
				console.error('ERROR GETTING COLLECTION NAMES', e)
			}
		}
		if (collectionNames.length === 0) {
			getCollectionNames()
		}
	}, [])

	// Auth effects
	useEffect(() => {
		const isUserAuthenticated = () => {
			return (
				isAuthenticated &&
				primaryWallet != null &&
				primaryWallet.address &&
				user != null
			)
		}
		if (isUserAuthenticated() && !showAuthFlow && performingLogin) {
			setPerformingLogin(false)
		}
	}, [isAuthenticated, performingLogin, primaryWallet, showAuthFlow, user])

	const handleLogin = () => {
		setPerformingLogin(true)
		setShowAuthFlow(true)
	}

	useEffect(() => {
		if (pendingSign) {
			const referenceNFTs = pendingSign.references.nft
				? pendingSign.references.nft
				: pendingSign.references.entry.nft
					? pendingSign.references.entry.nft
					: {}
			const pendingSignNFTProperties =
				referenceNFTs && pendingSign.template.slug === 'collection'
					? mapReferenceEntriesToFormData(referenceNFTs)
					: {}
			pendingSign.properties.nfts = pendingSignNFTProperties
			setFormData(pendingSign.properties)
			setAttestationState('pinned')
			setIsPinned(true)
			setPinData(pendingSign)
			const calculateNewCount = async nfts => {
				let newCount = 0
				await nfts.forEach(async nftEntry => {
					// TODO: add support for other input methods
					if (nftEntry.type === 'smartContractRange') {
						if (
							nftEntry.chain &&
							nftEntry.contract &&
							nftEntry.tokenStart &&
							nftEntry.tokenEnd
						) {
							const tokenStartNumber = Number(nftEntry.tokenStart)
							const tokenEndNumber = Number(nftEntry.tokenEnd)
							if (tokenStartNumber <= tokenEndNumber) {
								const tokenRange = tokenEndNumber - tokenStartNumber
								newCount += tokenRange + 1
							}
						}
					} else if (nftEntry.type === 'smartContractIDs') {
						if (nftEntry.chain && nftEntry.contract && nftEntry.tokenIDs) {
							const tokenIDsLength = nftEntry.tokenIDs.length
							newCount += tokenIDsLength
						}
					} else if (nftEntry.type === 'smartContractFulll') {
						if (nftEntry.chain && nftEntry.contract) {
							if (contractCache[nftEntry.contract]) {
								newCount += contractCache[nftEntry.contract]
							} else {
								if (
									nftEntry.chain === 'ethereum' ||
									nftEntry.chain === 'polygon' ||
									nftEntry.chain === 'shape' ||
									nftEntry.chain === 'base'
								) {
									if (isValidEthereumAddress(nftEntry.contract)) {
										const response = await fetch(`/collectionNFTCountAPI?contractAddress=${nftEntry.contract}&chain=${nftEntry.chain}`)
										const countAPIResponse = await response.json()
										if (countAPIResponse) {
											if (countAPIResponse.quantity) {
												newCount += Number(countAPIResponse.quantity)
												contractCache[nftEntry.contract] = Number(countAPIResponse.quantity)
											}
										}
									}
								}
							}
						}
					}
				})
				setNftsCount(newCount)
			}
			if (
				pendingSign.properties.nfts &&
				Array.isArray(pendingSign.properties.nfts)
			) {
				calculateNewCount(pendingSign.properties.nfts)
			}
			generatePreview(pendingSign)
		}
	}, [pendingSign])

	// Attestation Effects

	const validateRequiredFields = (
		val: MetadataProperties | {},
		isSubmit: boolean,
	) => {
		// @ts-ignore
		let returnedMissingFields: CollectionTemplate['fields'][] = []
		let returnedErrorFields = { ...errorFields }
		//@ts-ignore
		const requiredFields = CollectionTemplate.fields.filter(
			//@ts-ignore
			field => field.required === true && !field.hidden,
		)
		if (isSubmit) {
			returnedMissingFields = requiredFields.filter(
				//@ts-ignore
				requiredField =>
					//@ts-ignore
					!val[requiredField.slug] ||
					//@ts-ignore
					!val[requiredField.slug].length,
			)
		}

		// @ts-ignore
		const urlField = requiredFields.find(field => field.slug === 'url')
		const imageUrlField = requiredFields.find(
			field => field.slug === 'imageUrl',
		)

		// Todo: create explicit validation function
		if (urlField && val.hasOwnProperty('url')) {
			// @ts-ginore
			const formUrl = val.url
			// if (!isSubmit) {

			// } else {
			// const formUrl = val.url
			if (!URL.canParse(formUrl)) {
				returnedMissingFields.push(urlField)
			}
			// }
		}

		if (imageUrlField && val.hasOwnProperty('imageUrl')) {
			// @ts-ignore
			const formImageUrl = val.imageUrl
			if (!URL.canParse(formImageUrl)) {
				returnedMissingFields.push(imageUrlField)
			}
		}

		const nameField = requiredFields.find(field => field.slug === 'name')
		const slugField = requiredFields.find(field => field.slug === 'slug')
		if (isSubmit && (!formData.nfts || formData.nfts.length === 0)) {
			returnedErrorFields.nfts = 'Add NFTs'
		}
		if (val.nfts && val.nfts.length > 0) {
			if (returnedErrorFields.nfts) delete returnedErrorFields.nfts
		}

		if (nameField && val.hasOwnProperty('name')) {
			const formName = val.name
			if (
				formName &&
				collectionNames &&
				collectionNames.includes(formName.toLowerCase())
			) {
				returnedErrorFields.name =
					'This Collection Name is already taken, please select another'
			} else if (formName && formName !== '') {
				if (returnedErrorFields.name) {
					delete returnedErrorFields.name
				}
			}
		}
		if (slugField && val.hasOwnProperty('slug')) {
			const formSlug = val.slug
			if (formSlug && collectionSlugs && collectionSlugs.includes(formSlug)) {
				returnedErrorFields.slug =
					'This Collection Slug is already taken, please select another'
			} else if (formSlug && formSlug !== '') {
				if (returnedErrorFields.slug) {
					delete returnedErrorFields.slug
				}
			}
		}

		// @ts-ignore
		const errorFieldsLength = Object.values(returnedErrorFields).length
		if (returnedMissingFields.length > 0) {
			// missingFields.forEach(field => {
			//     setErrorFields({ ...errorFields, ...{ [field.slug]: `Please enter a valid ${field.name} value` } })
			// })
			setErrorMessage('Please fill in all the required fields.')
		} else if (errorFieldsLength > 0) {
			setErrorMessage('Please fix fields with errors.')
		} else {
			setErrorMessage('')
		}
		return { returnedErrorFields, returnedMissingFields }
	}

	const handleFormDataChange = (newVal: MetadataProperties | {}) => {
		if (setConfirmationModal)
			setConfirmationModal({
				title: 'Close without saving?',
				message:
					"To avoid losing the information you've entered, click on Back to Edit and then, click Continue. You can then close the form from the next step and your information will be securely stored.",
				acceptButton: 'Confirm and Close',
				cancelButton: 'Back to Edit',
			})

		const newCopiedVal = { ...newVal }
		setFormData({ ...newCopiedVal })
		const { returnedMissingFields, returnedErrorFields } =
			validateRequiredFields(newVal, false)
		// const missingFieldsLength = returnedMissingFields.length;
		// const errorFieldsLength = Object.values(returnedErrorFields).length;
		// @ts-ignore
		setMissingRequiredFields(returnedMissingFields)
		setErrorFields(returnedErrorFields)
	}

	const handlePrepareSign = () => {
		let signableMessage: string
		if (pinData) {
			const pendingSignNFTProperties =
				pinData.references.nft && pinData.template.slug === 'collection'
					? mapReferenceEntriesToFormData(pinData.references.nft)
					: pinData.references.entry?.nft &&
						  pinData.template.slug === 'collection'
						? mapReferenceEntriesToFormData(pinData.references.entry?.nft)
						: {}
			pinData.properties.nfts = pendingSignNFTProperties
			signableMessage = generateCollectionMessageToSign(pinData)
			setSigningMessage(signableMessage)
			if (signableMessage && signableMessage.length) {
				setErrorMessage('')
				handleSign(signableMessage)
			}
		} else {
			setErrorMessage(
				'Error generating signing Message because Pinned Data not recognized.',
			)
		}
	}

	const handleSign = async (signingMessage: string) => {
		// Check if user is authenticated
		if (!isAuthenticated) {
			setErrorMessage(
				'An error has occurred when attempting to validate your authentication status for signing.',
			)
			return
		}

		if (!primaryWallet) {
			setErrorMessage(
				'An error has occurred when attempting to retrieve your wallet address for signing.',
			)
			return
		}

		let signature
		setAttestationState('signing')
		try {
			signature = await primaryWallet.signMessage(signingMessage)
		} catch (e) {
			setAttestationState('sign error')
			if ((e as Error).message) {
				setErrorMessage(
					`An error has occurred when requesting your signature: ${(e as Error).message.split('\n')[0]}`,
				)
			} else {
				setErrorMessage(
					'An unexpected error has occurred when requesting your signature. Please try again.',
				)
			}
			setSigningMessage('')
			return
		}
		if (!signature) {
			setAttestationState('sign error')
			setErrorMessage(
				'An error has occurred when attempting to retrieve your signature.',
			)
			return
		}
		setSignature(signature)

		let payload = {
			...pinData,
			message: signingMessage,
			signature: signature,
		}

		try {
			const signResponse = await fetch('/signMetadataAPI', {
				method: 'POST',
				headers: {
					'Content-Type': 'application/json',
				},
				body: JSON.stringify({ payload }),
			})
			const jsonResponse = (await signResponse.json()) as {
				success: boolean
				data: any
			}
			if (jsonResponse.success === true && jsonResponse.data) {
				trackEvent('Client_Attestation_Attested', {
					template: jsonResponse.data.template.slug,
					address: jsonResponse.data.address,
					metadataIPFSCID: jsonResponse.data.references.metadataIPFSCID,
					attestationIPFSCID: jsonResponse.data.references.attestationIPFSCID,
				})
				setSignData(jsonResponse.data)
				setIsSigned(true)
				setFormData({})
				setPinData(null)
				setSignature('')
				setSigningMessage('')
				setAttestationState('signed')
				if (setSignedEntry) setSignedEntry(jsonResponse.data)
			} else {
				setSigningMessage('')
				setAttestationState('sign error')
				setErrorMessage(
					'An error has occurred when attempting to sign your entry. Please verify the provided information and try again, or contact support.',
				)
			}
		} catch (e) {
			setSigningMessage('')
			setAttestationState('sign error')
			setErrorMessage(
				'An unexpected error has occurred when attempting to sign your entry. Please try again or contact support.',
			)
		}
	}

	const handleBackToEdit = () => {
		setErrorMessage('')
		setSigningMessage('')
		if (pinData && pinData.properties) {
			// setFormData(pinData.properties)
			setAttestationState('editing')
			setIsPinned(false)
			setPinData(null)
			setPreviewPinData(undefined)
		} else if (signData && signData.properties) {
			// setFormData(signData.properties)
			setAttestationState('editing')
			setIsPinned(false)
			setPreviewPinData(undefined)
			setSignData(null)
		}
		if (setConfirmationModal)
			setConfirmationModal({
				title: 'Your information was saved',
				message:
					"All the information you've entered will be available in the Signatures section as a Draft in the Profile Manager. You can come back and continue signing at any time.",
				acceptButton: 'Close',
				cancelButton: null,
			})
		setPinData(null)
	}

	const generatePreview = (pinnedData: PinnedMetadataProperties) => {
		const pendingSignNFTProperties =
			pinnedData.references.nft && pinnedData.template.slug === 'collection'
				? mapReferenceEntriesToFormData(pinnedData.references.nft)
				: {}
		interface PreviewPinData extends PinnedMetadataProperties {
			userAvatar: string
			username: string
		}
		// @ts-ignore
		const userAvatar = getUserAvatar({ user, selectedArtist, formData })
		const username = getUserName({ user, selectedArtist, formData })
		// @ts-ignore
		const previewPinData: PreviewPinData = {
			...pinnedData,
			userAvatar,
			username,
		}
		if (previewPinData.properties && !previewPinData?.properties.nfts) {
			previewPinData.properties.nfts = pendingSignNFTProperties
		}
		setPreviewPinData(previewPinData)
		return previewPinData
	}

	const mapAndSubmitPin = async (isDraft?: boolean) => {
		let newFormData = { ...formData }
		setAttestationState('pinning')
		if (!primaryWallet || !primaryWallet.address) {
			setErrorMessage('An error occured retrieving your authenticated address')
			return
		}
		if (!user || !user.userId) {
			setErrorMessage('An error occured retrieving your authenticated userId')
			return
		}
		let ipfsImageURL = formData?.imageUrl || ''
		if (ipfsImageURL) {
			toast.custom(
				t => (
					<Toast
						variant={'uploading'}
						//@ts-ignore
						content={'Storing image in web3 storage'}
						onClose={() => toast.dismiss(t)}
					/>
				),
				{ position: 'bottom-right' },
			)
			try {
				const imageFormData = new FormData()
				imageFormData.append('imageUrl', ipfsImageURL)

				const arweaveTags = {
					'App-Name': import.meta.env.VITE_APP_NAME,
					'App-Version': version,
					Type: 'Media',
					Topic: 'Attestations',
					// Calculated and added in /linkImageUploadAPI endpoint
					'Content-Type': '',
					'AF-Template': CollectionTemplateType.name,
					'AF-Template-Version': CollectionTemplateType.version,
					...(import.meta.env.VITE_NODE_ENV &&
						import.meta.env.VITE_NODE_ENV === 'development' && {
							Environment: 'development',
						}),
				}
				imageFormData.append('tags', JSON.stringify(arweaveTags))
				try {
					const options = {
						method: 'POST',
						headers: { 'Conent-Type': 'application/json' },
						body: imageFormData,
					}
					const linkImageUploadResponse = await fetch(
						'/linkImageUploadAPI',
						options,
					)
					const linkImageUploadResponseJson =
						await linkImageUploadResponse.json()
					if (newFormData && linkImageUploadResponseJson.ipfsCID) {
						newFormData.imageUrl = `${import.meta.env.VITE_IPFS_NODE}/${linkImageUploadResponseJson.ipfsCID}`
						setFormData(newFormData)
					}
					toast.custom(
						t => (
							<Toast
								variant={'success'}
								//@ts-ignore
								content={'Stored image in web3 storage'}
								onClose={() => toast.dismiss(t)}
							/>
						),
						{ position: 'bottom-right' },
					)
				} catch (error) {
					console.error('INNER ERROR STORING IMAGE UPLOAD LINK', error)
				}
			} catch (error) {
				console.error('OUTER ERROR STORING IMAGE UPLOAD LINK', error)
			}
		}
		const newPinData = await mapPinData({
			selectedForm: CollectionTemplate,
			address: primaryWallet?.address,
			userId: user?.userId,
			data: newFormData,
		})

		if (newPinData) {
			try {
				toast.custom(
					t => (
						<Toast
							variant={'uploading'}
							//@ts-ignore
							content={'Adding Collection Attestation to Web3 Storage'}
							onClose={() => toast.dismiss(t)}
						/>
					),
					{ position: 'bottom-right' },
				)
				const { pinnedData, draftAttestationUid } = await handlePin({
					pinData: newPinData,
				})
				setAttestationUid(draftAttestationUid)
				toast.custom(
					t => (
						<Toast
							variant={'success'}
							//@ts-ignore
							content={'Added Collection Attestation to Web3 Storage'}
							onClose={() => toast.dismiss(t)}
						/>
					),
					{ position: 'bottom-right' },
				)

				if (pinnedData) {
					if (isDraft) {
						toast.custom(
							t => (
								<Toast
									variant={'success'}
									//@ts-ignore
									content={'Added Collection Attestation to Web3 Storage'}
									onClose={() => toast.dismiss(t)}
								/>
							),
							{ position: 'bottom-right' },
						)
						setShowPostSaveDraftModal(true)
						setAttestationState('editing')
						// TODO: decide what we show after saving draft
						return
					}
					setAttestationState('pinned')
					setIsPinned(true)
					const references = pinnedData.references.entry
						? pinnedData.references.entry
						: pinnedData.references

					trackEvent('Client_Attestation_Pinned', {
						template: pinnedData.template.slug,
						metadataIPFSCID: pinnedData.references.metadataIPFSCID,
						...(references &&
							references.nft && {
								collection_nfts: references.nft,
							}),
					})
					if (
						references.nft &&
						Object.keys(references.nft).includes('smartContractFull')
					) {
						const loadingCollectionEntryCounts =
							getLoadingCollectionEntryCounts(pinnedData, true)
						const collectionNFTCounts = loadingCollectionEntryCounts.reduce(
							(prev, curr) => {
								return (prev += curr.tokenCount)
							},
							0,
						)
						setNftsCount(collectionNFTCounts)
						setPinData({
							...pinnedData,
							collectionEntries: loadingCollectionEntryCounts,
						})
						generatePreview({
							...pinnedData,
							collectionEntries: loadingCollectionEntryCounts,
						})
						try {
							const enrichedPin = await getCollectionEntryCounts({
								pinData: pinnedData,
							})
							if (
								enrichedPin &&
								enrichedPin.success === true &&
								enrichedPin.pinData &&
								enrichedPin.pinData.collectionEntries
							) {
								const collectionNFTCountsAwaited =
									enrichedPin.pinData.collectionEntries.reduce((prev, curr) => {
										return (prev += curr.tokenCount)
									}, 0)
								setNftsCount(collectionNFTCountsAwaited)
								setPinData(enrichedPin.pinData)
								generatePreview(enrichedPin.pinData)
							}
						} catch (e) {
							console.error('Eerror getting collection entry counts', e)
						}
					} else {
						const loadingCollectionEntryCounts =
							getLoadingCollectionEntryCounts(pinnedData, false)
						setPinData({
							...pinnedData,
							collectionEntries: loadingCollectionEntryCounts,
						})
						const collectionNFTCounts = loadingCollectionEntryCounts.reduce(
							(prev, curr) => {
								return (prev += curr.tokenCount)
							},
							0,
						)
						setNftsCount(collectionNFTCounts)
						generatePreview({
							...pinnedData,
							collectionEntries: loadingCollectionEntryCounts,
						})
					}
					if (setConfirmationModal)
						setConfirmationModal({
							title: 'You haven’t signed this entry',
							message:
								"If you close this form without signing your attestation, all the information you've entered will be securely stored in the Signatures section as a Draft in the Profile Manager.",
							acceptButton: 'Close and sign later',
							cancelButton: 'Sign now',
						})
				} else {
					setErrorMessage(
						'An error has occurred when attempting to store your entry. Please verify the provided information and try again, or contact support.',
					)
					setAttestationState('pin error')
				}
			} catch (e) {
				setErrorMessage(
					'An error has occurred when attempting to store your entry. Please verify the provided information and try again, or contact support.',
				)
				setAttestationState('pin error')
			}
		} else {
			setErrorMessage(
				'An error has occurred when preparing to store your entry. Please verify the provided information and try again, or contact support.',
			)
			setAttestationState('pin error')
		}
	}

	const handleSubmit = async () => {
		const { returnedErrorFields, returnedMissingFields } =
			validateRequiredFields(formData, true)
		const missingFieldsLength = returnedMissingFields.length
		const errorFieldsLength = Object.values(returnedErrorFields).length
		// @ts-ignore
		setMissingRequiredFields(returnedMissingFields)
		setErrorFields(returnedErrorFields)
		const isValid = missingFieldsLength === 0 && errorFieldsLength === 0
		if (isValid) {
			mapAndSubmitPin()
		} else {
			toast.custom(
				t => (
					<Toast
						variant={'error'}
						//@ts-ignore
						content={
							missingFieldsLength > 0
								? 'Fill out all required fields'
								: 'Fix fields with errors'
						}
						onClose={() => toast.dismiss(t)}
					/>
				),
				{ position: 'bottom-right' },
			)
		}
	}

	const handleShowSaveDraftModal = () => {
		const { returnedErrorFields, returnedMissingFields } =
			validateRequiredFields(formData, true)
		const missingFieldsLength = returnedMissingFields.length
		const errorFieldsLength = Object.values(returnedErrorFields).length
		// @ts-ignore
		setMissingRequiredFields(returnedMissingFields)
		setErrorFields(returnedErrorFields)
		const isValid = missingFieldsLength === 0 && errorFieldsLength === 0
		if (isValid) {
			setShowPreSaveDraftModal(true)
		} else {
			toast.custom(
				t => (
					<Toast
						variant={'error'}
						//@ts-ignore
						content={
							missingFieldsLength > 0
								? 'Fill out all required fields'
								: 'Fix fields with errors'
						}
						onClose={() => toast.dismiss(t)}
					/>
				),
				{ position: 'bottom-right' },
			)
		}
	}

	const handleSaveDraft = () => {
		const { returnedErrorFields, returnedMissingFields } =
			validateRequiredFields(formData, true)
		const missingFieldsLength = returnedMissingFields.length
		const errorFieldsLength = Object.values(returnedErrorFields).length
		// @ts-ignore
		setMissingRequiredFields(returnedMissingFields)
		setErrorFields(returnedErrorFields)
		const isValid = missingFieldsLength === 0 && errorFieldsLength === 0
		if (isValid) {
			mapAndSubmitPin(true)
			setShowPreSaveDraftModal(false)
		} else {
			toast.custom(
				t => (
					<Toast
						variant={'error'}
						//@ts-ignore
						content={
							missingFieldsLength > 0
								? 'Fill out all required fields'
								: 'Fix fields with errors'
						}
						onClose={() => toast.dismiss(t)}
					/>
				),
				{ position: 'bottom-right' },
			)
		}
	}

	return (
		<div
			className={clsx('w-full max-w-[1148px]', 'transition-width ease-linear')}
		>
			{!isSigned && <CollectionHeadComponent />}
			{!isSigned && (
				<CollectionAddNFTsComponent
					theme={theme}
					nftCount={nftsCount}
					attestationState={attestationState}
					formData={formData}
					handleFormDataChange={handleFormDataChange}
					errorFields={errorFields}
				/>
			)}
			{!isSigned && !isPinned && (
				<CollectionDetailsComponent
					user={user}
					users={users}
					isImageUploading={isImageUploading}
					setIsImageUploading={setIsImageUploading}
					theme={theme}
					formData={formData}
					selected={selectedArtist}
					setSelected={setSelectedArtist}
					missingRequiredFields={missingRequiredFields}
					errorFields={errorFields}
					handleFormDataChange={handleFormDataChange}
				/>
			)}

			{isPinned && pinData && !isSigned && (
				<>
					<div className="mt-8 flex flex-col gap-x-4 gap-y-2 md:flex-row">
						<div className="flex flex-col pr-[93px] md:w-2/5">
							<TypographyHeading
								size="medium"
								isBold={true}
								additionalClasses="text-textPrimaryLight dark:text-textPrimaryDark"
							>
								{attestationState &&
								['pinning', 'pin error', 'pinned'].includes(attestationState)
									? 'Does everything look good?'
									: null}
								{attestationState &&
								attestationState === 'signing' &&
								!signature
									? 'Requesting Signature.'
									: null}
								{signature ? 'Creating Attestation.' : null}
							</TypographyHeading>
							<TypographyParagraph
								size="medium"
								additionalClasses="mt-2 text-textSecondaryLight dark:text-textSecondaryDark"
							>
								{attestationState &&
								['pinning', 'pin error', 'pinned'].includes(attestationState)
									? 'Please carefully review your Collection details before signing. Once confirmed, you will be prompted to sign a message from your wallet provider (ex: MetaMask).'
									: null}
								{attestationState &&
								attestationState === 'signing' &&
								!signature
									? 'Sign the message from your wallet provider to create this Collection.'
									: null}
								{signature
									? 'Please don’t close this window until we’ve made your art immortal.'
									: null}
							</TypographyParagraph>
							{signature ? <Loader size="small" /> : null}
						</div>
						<div className="flex flex-col md:w-3/5">
							{previewPinData && (
								<Timeline
									//@ts-ignore
									metadataEntries={[{ ...previewPinData, attestationUid }]}
									isPreview={true}
									metadataAdditionalClasses="w-full"
									tweetsUrl={`${import.meta.env.VITE_VERCEL_URL}/getTweet`}
									displayAvatars={true}
								/>
							)}
							<InternalLink
								to={getCollectionPreviewURL({
									...previewPinData,
									attestationUid,
								})}
								target="_blank"
								additionalClasses="w-full mt-4"
							>
								<Callout
									variant="warning"
									content={
										<>
											<b>Preview your collection</b>
											<span>
												{' '}
												before signing to ensure all information is correct.
												Signed collections create immutable records.
											</span>
										</>
									}
									additionalClasses="w-full"
								/>
							</InternalLink>
						</div>
					</div>
				</>
			)}
			{/*  */}

			{isSigned && signData && signData.references.attestationIPFSCID && (
				<>
					<div className="mt-8 flex flex-col gap-x-4 gap-y-2 md:flex-row">
						<div className="flex flex-col pr-[70px] md:w-2/5">
							<TypographyCaps
								size="medium"
								additionalClasses="text-green500 mb-4"
							>
								SUCCESS!
							</TypographyCaps>
							<TypographyHeading
								size="medium"
								isBold={true}
								additionalClasses="text-textPrimaryLight dark:text-textPrimaryDark"
							>
								You've successfully created a new verified Collection!
							</TypographyHeading>
							<TypographyParagraph
								size="medium"
								additionalClasses="mt-2 text-textSecondaryLight dark:text-textSecondaryDark"
							>
								Your Collection has been successfully verified, signed and
								securely store onchain. The Onchain History of your Collection
								will update shortly.
							</TypographyParagraph>
							<div className="mt-4">
								<a
									href={getShareAttestationToXURL(signData, atomicLoreUrl)}
									target="_blank"
									rel="noreferrer"
								>
									<Button variant="tertiary" leftIcon={<XIcon />} size="medium">
										Share on Twitter
									</Button>
								</a>
							</div>
						</div>
						<div className="flex flex-col md:w-3/5">
							<CollectionCard
								name={signData.properties.name}
								artist={getUserName({ user, selectedArtist, formData })}
								description={''}
								blockchain={getCollectionBlockchainNamesString(
									signData.properties && signData.properties.nfts
										? signData.properties.nfts
										: pinData?.properties.nfts
											? pinData.properties.nfts
											: [],
								)}
								count={nftsCount}
								image={
									signData.properties && signData.properties.imageUrl
										? signData.properties.imageUrl
										: ''
								}
								hasHoverEffect={true}
								cardAdditionalProps={'w-full h-[446px] object-cover'}
								containerAdditionalProps={''}
								imageAdditionalProps={'w-full h-[330px]'}
							/>
							<Link
								className="mb-2 mt-10"
								to={`/collectionProfile/${signData.references.attestationIPFSCID}`}
								prefetch="none"
							>
								<Button
									variant="primary"
									size="medium"
									additionalClasses="w-full"
								>
									View Collection Profile
								</Button>
							</Link>
							<Link
								to={`/metadataProfile/collection/${signData.references.attestationIPFSCID}`}
								prefetch="none"
							>
								<Button
									variant="secondary"
									size="medium"
									additionalClasses="w-full"
								>
									View Provenance
								</Button>
							</Link>
						</div>
					</div>
				</>
			)}
			{(!signData || !signData.references.attestationIPFSCID) && (
				<div className="mt-8 flex flex-row gap-x-4">
					<div className="flex w-0 flex-col md:w-2/5">
						{/*<TypographyHeading size="small" additionalClasses="text-textPrimaryLight dark:text-textPrimaryDark">3. Store and sign</TypographyHeading>*/}
					</div>
					<div className="flex w-full flex-col md:!w-3/5">
						{errorMessage && (
							<Callout
								variant="error"
								content={errorMessage}
								additionalClasses="w-full mb-4"
							/>
						)}
						{!isAuthenticated && (
							<Button
								variant="primary"
								additionalClasses="w-full"
								onClick={handleLogin}
							>
								Connect Wallet
							</Button>
						)}
						{isAuthenticated &&
							!isPinned &&
							!signingMessage.length &&
							!signData && (
								<div className="flex w-full flex-col">
									<Button
										variant="primary"
										additionalClasses="w-full"
										disabled={
											attestationState === 'pinning' || isImageUploading
										}
										onClick={() => {
											if (primaryWallet) {
												handleSubmit()
											} else {
												setShowDynamicUserProfile(true)
											}
											// setAttestationState("pinning")
											// setErrorMessage('')
											// validateRequiredFields()
										}}
									>
										{attestationState === 'pinning'
											? 'Loading...'
											: primaryWallet
												? 'Continue'
												: 'Connect Wallet'}
									</Button>

									<Button
										variant="secondary"
										additionalClasses="w-full my-2"
										disabled={attestationState === 'pinning' || !primaryWallet}
										onClick={() => {
											if (primaryWallet) {
												handleShowSaveDraftModal()
											} else {
												setShowDynamicUserProfile(true)
											}
											// setAttestationState("pinning")
											// setErrorMessage('')
											// validateRequiredFields()
										}}
									>
										Save as Draft
									</Button>
								</div>
							)}
						{isAuthenticated &&
							['pinned', 'signing', 'sign error'].includes(attestationState) &&
							!signData && (
								<>
									<Button
										variant="primary"
										leftIcon={<SignIcon />}
										additionalClasses="w-full"
										disabled={
											signingMessage.length != 0 ||
											attestationState === 'signing'
										}
										onClick={handlePrepareSign}
									>
										Sign
									</Button>
									<Button
										variant="secondary"
										additionalClasses="w-full mt-2"
										disabled={
											signingMessage.length != 0 ||
											attestationState === 'signing'
										}
										onClick={handleBackToEdit}
									>
										Back to edit
									</Button>
								</>
							)}
					</div>
				</div>
			)}
			<Modal
				size="large"
				title="Save Your Collection Draft"
				isOpen={showPreSaveDraftModal}
				onClose={() => setShowPreSaveDraftModal(false)}
				body={
					<div className="flex w-full flex-col items-center gap-y-4">
						<TypographyParagraph
							size="medium"
							additionalClasses="text-textPrimaryLight dark:text-textPrimaryDark"
						>
							Your progress will be saved privately, allowing you to preview and
							perfect your collection's story before publishing. You can access
							and edit your drafts anytime from your{' '}
							<span className="text-paragraphMediumBold">
								Signatures history
							</span>{' '}
							in your profile..
						</TypographyParagraph>
						<Button
							variant="primary"
							size="large"
							additionalClasses="w-full"
							onClick={() => handleSaveDraft()}
						>
							Save Draft
						</Button>
						<Button
							variant="secondary"
							size="large"
							additionalClasses="w-full"
							onClick={() => setShowPreSaveDraftModal(false)}
						>
							Close
						</Button>
					</div>
				}
			></Modal>
			<Modal
				size="large"
				title="Draft Saved"
				titleIcon={<CompletedIcon height="32" width="32" />}
				isOpen={showPostSaveDraftModal}
				onClose={() => setShowPostSaveDraftModal(false)}
				body={
					<div className="flex w-full flex-col items-center gap-y-4">
						<TypographyParagraph
							size="medium"
							additionalClasses="text-textPrimaryLight dark:text-textPrimaryDark"
						>
							Your "{formData.name || 'Untitled'}" collection is safely stored
							as Draft. You can return to edit and publish it anytime.
						</TypographyParagraph>
						<Button
							variant="primary"
							size="large"
							additionalClasses="w-full"
							onClick={() => {
								setShowPostSaveDraftModal(false)
								navigate('/userProfile?view=signatures&selection=drafts')
							}}
						>
							View Drafts
						</Button>
						<Button
							variant="secondary"
							size="large"
							additionalClasses="w-full"
							onClick={() => {
								navigate('.', { replace: true })
							}}
						>
							Start New
						</Button>
						<Button
							variant="secondary"
							size="large"
							additionalClasses="w-full"
							onClick={() => setShowPostSaveDraftModal(false)}
						>
							Continue Editing
						</Button>
					</div>
				}
			></Modal>
		</div>
	)
}

export const CollectionHeadComponent = () => {
	const [performingLogin, setPerformingLogin] = useState(false)
	const {
		primaryWallet,
		user,
		showAuthFlow,
		setShowAuthFlow,
		setShowDynamicUserProfile,
	} = useDynamicContext()
	const { trackEvent } = useAnalytics()
	const isAuthenticated = useIsLoggedIn()

	// Auth effects
	useEffect(() => {
		const isUserAuthenticated = () => {
			return (
				isAuthenticated &&
				primaryWallet != null &&
				primaryWallet.address &&
				user != null
			)
		}
		if (isUserAuthenticated() && !showAuthFlow && performingLogin) {
			setPerformingLogin(false)
		}
	}, [isAuthenticated, performingLogin, primaryWallet, showAuthFlow, user])

	const handleLogin = () => {
		setPerformingLogin(true)
		setShowAuthFlow(true)
	}

	return (
		<>
			<BackButton />
			<div className="mb-20 mt-9 flex flex-col justify-between md:flex-row">
				<div className="flex flex-col">
					<TypographyLabel
						size="small"
						additionalClasses="text-textSecondaryLight dark:text-textSecondaryDark"
					>
						ATOMIC LORE
					</TypographyLabel>
					<div className="my-2 flex text-textPrimaryLight dark:text-textPrimaryDark">
						<TypographyDisplay
							size="medium"
							additionalClasses="font-rapidSpecial text-textPrimaryLight dark:text-textPrimaryDark mr-2"
						>
							Create Collection
						</TypographyDisplay>
						<CertifyIcon size={48} />
					</div>
					<TypographyParagraph
						size="medium"
						additionalClasses="text-textSecondaryLight dark:text-textSecondaryDark"
					>
						By creating and signing Collections in Atomic Lore, you can build a
						verified Onchain archive of your artwork.
					</TypographyParagraph>
				</div>
				<div className="flex flex-col items-end justify-end">
					<DynamicUserProfile />
					{isAuthenticated && (
						<Button
							variant="secondary"
							onClick={() => setShowDynamicUserProfile(true)}
							rightIcon={<CaretDown />}
						>
							{primaryWallet && primaryWallet.address
								? truncateStringInMiddle(primaryWallet.address)
								: user && user.username
									? truncateStringInMiddle(user.username)
									: ''}
						</Button>
					)}
					{!isAuthenticated && (
						<Button
							variant="secondary"
							disabled={true}
							rightIcon={<WalletIcon />}
							onClick={() => handleLogin()}
						>
							Not connected
						</Button>
					)}
				</div>
			</div>
		</>
	)
}

export const CollectionAddNFTsComponent = ({
	theme,
	nftCount,
	attestationState,
	formData,
	errorFields,
	handleFormDataChange,
}: {
	theme: Theme
	nftCount?: number
	attestationState?: AttestationState
	formData: MetadataProperties | {}
	errorFields: { [key: string]: { [key: string]: string } }
	handleFormDataChange: (newVal: MetadataProperties | {}) => void
}) => {
	const [addNFTsManually, setAddNFTsManually] = useState(false)
	const [isAutofillModalOpen, setIsAutofillModalOpen] = useState(false)
	const [pendingAutofillData, setIsPendingAutofillData] = useState<
		CollectionResponse['collection'] | undefined
	>(undefined)
	const [collectionURLInput, setCollectionURLInput] = useState('')
	const [marketplaceCollectionsObject, setMarketplaceCollectionsObject] =
		useState<{ [key: string]: CollectionResponse }>({})
	const [isValidCollectionURL, setIsValidCollectionURL] = useState(false)
	const [isFetchingCollectionURL, setIsFetchingCollectionURL] = useState(false)
	const [hasCollectionURLError, setHasCollectionURLError] = useState(false)
	const [collectionURLError, setCollectionURLError] = useState<
		string | undefined
	>(undefined)
	const [editingEntry, setEditingEntry] = useState<
		CollectionNFTInputType | undefined
	>(undefined)

	const handleCollectionURLInputUpdate = (val: string) => {
		let checkingVal = val
		if (checkingVal.includes('https://opensea.io/collection/')) {
			checkingVal = checkingVal.slice(30)
		}

		setCollectionURLInput(checkingVal)
		try {
			new URL(`https://opensea.io/collection/${checkingVal}`)
			if (
				marketplaceCollectionsObject[
					`https://opensea.io/collection/${checkingVal}`
				]
			) {
				setIsValidCollectionURL(false)
				setHasCollectionURLError(true)
				setCollectionURLError('This Collection has already been added')
			} else {
				setIsValidCollectionURL(true)
				setHasCollectionURLError(false)
				setCollectionURLError(undefined)
			}
		} catch {
			setIsValidCollectionURL(false)
		}
	}

	const submitCollectionURL = async () => {
		setIsFetchingCollectionURL(true)
		toast.custom(
			t => (
				<Toast
					variant={'uploading'}
					//@ts-ignore
					content={
						'Searching for Collection, this may take up to 15 seconds depending on the Collection. Please wait for the confirmation to appear.'
					}
					onClose={() => toast.dismiss(t)}
				/>
			),
			{ position: 'bottom-right' },
		)
		const options = {
			method: 'GET',
			headers: { 'Content-Type': 'application/json' },
		}
		try {
			const response = await fetch(
				`/nftsByMarketplaceCollectionAPI?collectionSlug=${collectionURLInput}`,
				options,
			)
			const collectionResponse = (await response.json()) as CollectionResponse
			if (!collectionResponse) {
				setIsFetchingCollectionURL(false)
				setHasCollectionURLError(true)
				setCollectionURLError(
					'An error occurred fetching this Collection. Collection URLs are case sensitive, please double check and try again',
				)
			} else if (
				collectionResponse.message &&
				collectionResponse.success === false
			) {
				setIsFetchingCollectionURL(false)
				setHasCollectionURLError(true)
				setCollectionURLError(collectionResponse.message)
			} else if (
				collectionResponse.collection &&
				collectionResponse.inputArray
			) {
				let newData = { ...formData }
				let newNFTEntries: CollectionNFTInputType[] = newData.nfts
					? [...newData.nfts]
					: []
				let highestEntryId = 0
				if (newNFTEntries.length > 0) {
					try {
						const ids = [...newNFTEntries]
							.filter(
								entry =>
									entry.inputSelector &&
									entry.inputSelector.split('-')[1] &&
									!isNaN(Number(entry.inputSelector.split('-')[1])),
							)
							.map(entry =>
								entry.inputSelector
									? Number(entry.inputSelector.split('-')[1])
									: undefined,
							)
							.filter(id => id !== undefined)
						const maxInputId = Math.max(...ids)
						if (!isNaN(maxInputId)) {
							highestEntryId = maxInputId
						} else {
							highestEntryId = newNFTEntries.length + 1
						}
					} catch (e) {
						highestEntryId = newNFTEntries.length + 1
					}
				}
				const collectionInputsWithUpdatedIds =
					collectionResponse.inputArray.map((input: CollectionNFTInputType) => {
						highestEntryId += 1
						const updatedInput = {
							...input,
							inputSelector: `$nfts-${highestEntryId}-${input.type}-marketplace-${collectionURLInput}`,
						}
						return updatedInput
					})
				const newMarketplaceCollectionsObject = {
					...marketplaceCollectionsObject,
				}
				const collectionKey = `https://opensea.io/collection/${collectionURLInput}`
				newMarketplaceCollectionsObject[collectionKey] = {
					...collectionResponse,
					inputArray: collectionInputsWithUpdatedIds,
				}

				let newMarketplaceURLArray = []
				let newMarketplaceURLs: undefined | string = undefined
				for (const key in newMarketplaceCollectionsObject) {
					newMarketplaceURLArray.push(key)
				}
				if (newMarketplaceURLArray.length > 0) {
					newMarketplaceURLs = newMarketplaceURLArray.toString()
				}

				setMarketplaceCollectionsObject({ ...newMarketplaceCollectionsObject })
				setIsPendingAutofillData(collectionResponse.collection)
				setIsFetchingCollectionURL(false)
				setIsAutofillModalOpen(true)
				setCollectionURLInput('')
				newNFTEntries = [...newNFTEntries].concat([
					...collectionInputsWithUpdatedIds,
				])
				const newerData = {
					...newData,
					nfts: newNFTEntries,
					marketplaceUrls: newMarketplaceURLs,
				}
				handleFormDataChange(newerData)
				toast.custom(
					t => (
						<Toast
							variant={'success'}
							//@ts-ignore
							content={'Successfully retrieved Collection'}
							onClose={() => toast.dismiss(t)}
						/>
					),
					{ position: 'bottom-right' },
				)
			} else {
				setIsFetchingCollectionURL(false)
				setHasCollectionURLError(true)
				setCollectionURLError(
					'An error occurred fetching this Collection. Collection URLs are case sensitive, please double check and try again',
				)
			}
		} catch (e) {
			console.error('FETCH COLLECTION ERROR', e)
			setIsFetchingCollectionURL(false)
			const message =
				e && e.message
					? e.message
					: 'An error occurred fetching this Collection'
			setCollectionURLError(
				message +
					'.  Collection URLs are case sensitive, please double check and try again',
			)
			setHasCollectionURLError(true)
		}
	}

	const updateFormWithMarketplaceCollectionData = (
		collection: CollectionResponse['collection'],
	) => {
		let newData = { ...formData }
		const newerData = {
			...newData,
			...(collection && {
				name: collection.name,
				description: collection.description,
				slug: slugify(collection.name, {
					lower: true,
					strict: true,
					trim: false,
				}),
			}),
			...(collection &&
				collection.banner_image_url && {
					imageUrl: collection.banner_image_url,
				}),
			...(collection &&
				collection.deployment_date && {
					date: collection.deployment_date,
				}),
			...(collection &&
				!collection.banner_image_url &&
				collection.image_url && {
					imageUrl: collection.image_url,
				}),
			...(collection &&
				collection.external_url && {
					url: collection.external_url,
				}),
		}
		handleFormDataChange(newerData)
	}

	const handleInputDataChange = (value: CollectionNFTInputType) => {
		const isUpdate = editingEntry !== undefined ? true : false
		let newData = { ...formData }
		const newNFTEntries: CollectionNFTInputType[] = newData.nfts
			? [...newData.nfts]
			: []
		if (isUpdate && editingEntry !== undefined) {
			let editingIndex: number | undefined
			for (let i = 0; i < newNFTEntries.length; i++) {
				const entry = newNFTEntries[i]
				if (entry.inputSelector === editingEntry.inputSelector) {
					editingIndex = i
					break
				}
			}
			if (editingIndex !== undefined && editingIndex >= 0) {
				newNFTEntries.splice(editingIndex, 1, value)
			}
		} else {
			newNFTEntries.push(value)
		}
		const newerData = { ...newData, nfts: newNFTEntries }
		handleFormDataChange(newerData)
	}

	const editCollectionEntry = (key: string) => {
		let newData = { ...formData }
		const newNFTEntries: CollectionNFTInputType[] =
			newData.nfts && Array.isArray(newData.nfts) ? [...newData.nfts] : []
		const newEdityEntry = newNFTEntries.find(
			entry => entry.inputSelector === key,
		)
		setEditingEntry(newEdityEntry)
		setAddNFTsManually(true)
	}

	const deleteMarketplaceCollectionEntry = (key: string) => {
		let newData = { ...formData }
		const newNFTEntries =
			newData.nfts && Array.isArray(newData.nfts) ? [...newData.nfts] : []
		const marketplaceEntryInputs = marketplaceCollectionsObject[key].inputArray
		const newerEntries = [...newNFTEntries].filter(entry => {
			if (marketplaceEntryInputs) {
				return !marketplaceEntryInputs.find(
					marketEntry => marketEntry.inputSelector === entry.inputSelector,
				)
			} else {
				return true
			}
		})

		const newMarketplaceCollectionsObject = { ...marketplaceCollectionsObject }
		delete newMarketplaceCollectionsObject[key]
		setMarketplaceCollectionsObject({ ...newMarketplaceCollectionsObject })
		let newMarketplaceURLArray = []
		let newMarketplaceURLs: undefined | string = undefined
		for (const key in newMarketplaceCollectionsObject) {
			newMarketplaceURLArray.push(key)
		}
		if (newMarketplaceURLArray.length > 0) {
			newMarketplaceURLs = newMarketplaceURLArray.toString()
		}

		const newerData = {
			...newData,
			nfts: [...newerEntries],
			marketplaceUrls: newMarketplaceURLs,
		}
		handleFormDataChange(newerData)
	}

	const deleteCollectionEntry = (key: string) => {
		let newData = { ...formData }
		const newNFTEntries =
			newData.nfts && Array.isArray(newData.nfts) ? [...newData.nfts] : []

		const newerEntries = [...newNFTEntries].filter(
			entry => entry.inputSelector !== key,
		)
		const newerData = { ...newData, nfts: [...newerEntries] }
		handleFormDataChange(newerData)
	}

	const getCollectionTypeName = (type: string) => {
		// @ts-ignore
		if (CollectionNFTInputTypeNameMap[type]) {
			// @ts-ignore
			return CollectionNFTInputTypeNameMap[type]
		} else {
			return type
		}
	}

	const getEntryDescriptionString = (entry: CollectionNFTInputType) => {
		if (entry.type === 'smartContractIDs') {
			return `${entry.tokenIDs ? ` - ${entry.tokenIDs.length}${entry.tokenIDs.length > 1 ? ' items' : ' item'}` : ''}`
		} else if (entry.type === 'smartContractRange') {
			const tokenEnd = Number(entry.tokenEnd)
			const tokenStart = Number(entry.tokenStart)
			const tokenRangeLength =
				tokenEnd === tokenStart ? 1 : tokenEnd - tokenStart + 1
			return ` - ${tokenRangeLength}${tokenRangeLength > 1 ? ' items' : ' item'}`
		} else if (entry.type === 'smartContractFull') {
			return ' - 1 contract'
		} else {
			return ''
		}
	}

	const getCollectionDescriptionString = (collection: CollectionType | {}) => {
		const count = nftCount || 0
		const chains = getCollectionBlockchainNamesString(
			collection && collection.nfts ? collection.nfts : [],
		)
		return `${chains} - ${count} ${count > 1 ? 'items' : 'item'}`
	}

	return (
		<div className="mt-8 flex flex-col gap-x-4 gap-y-2 border-b border-textInactiveLight pb-10 dark:border-textInactiveDark md:flex-row">
			<div
				className={clsx(
					attestationState && nonEditStates.includes(attestationState)
						? 'pr-[93px]'
						: 'pr-[70px]',
					'flex flex-col md:w-2/5',
				)}
			>
				{attestationState === 'editing' && (
					<TypographyHeading
						size="medium"
						isBold={true}
						additionalClasses="mb-2 text-textPrimaryLight dark:text-textPrimaryDark"
					>
						Add NFTs to Collection
					</TypographyHeading>
				)}
				<div className="flex flex-row items-center">
					<TypographyParagraph
						size="medium"
						additionalClasses="text-textSecondaryLight dark:text-textSecondaryDark"
					>
						{/* Import an existing collection with a marketplace URL or tap “Add NFTs manually” to associate NFTs in this Collection. */}
						{attestationState === 'editing'
							? 'Import an existing Collection with a marketplace URL or tap “Add NFTs manually” to associate NFTs in this Collection.'
							: null}
						{attestationState && nonEditStates.includes(attestationState)
							? 'Sign this Collection to certify it, along with all the NFTs included in the Collection.'
							: null}
					</TypographyParagraph>
				</div>{' '}
			</div>
			{DynamicContext._currentValue ? (
				<div className="flex flex-col md:w-3/5">
					<div
						className={clsx(
							'mb-4',
							errorFields.nfts
								? 'fill-red stroke-red text-red dark:fill-red dark:stroke-red dark:text-red'
								: '',
						)}
					>
						{attestationState === 'editing' ? (
							<>
								<div className="flex flex-col gap-y-2">
									<TypographyLabel
										size="medium"
										additionalClasses="text-textPrimaryLight dark:text-textPrimaryDark"
										isBold={true}
									>
										Marketplace URL
									</TypographyLabel>
									<TypographyParagraph
										size="small"
										additionalClasses="text-textSecondaryLight dark:text-textSecondaryDark"
									>
										Complete the URL for the Opensea Collection, then tap "Add."
									</TypographyParagraph>
									<div className="mb-4 flex gap-x-2">
										<InlineURLInputField
											name="Marketplace Collection URL"
											type="slug"
											id="marketplaceCollectionUrl"
											displayURL="https://opensea.io/collection/"
											displayRequired={false}
											displayName={false}
											containerWidth="w-full"
											value={collectionURLInput}
											onChange={e =>
												handleCollectionURLInputUpdate(e.target.value)
											}
											helper={collectionURLError}
											jsxHelper={
												<div className="mt-[12.5px] flex items-center gap-x-2 text-textPrimaryLight dark:text-textPrimaryDark">
													<TypographyLabel size="small">
														We currently support links from:{' '}
													</TypographyLabel>
													<OpenSeaIcon />
												</div>
											}
											isError={hasCollectionURLError}
										/>
										{/* <InputField
											name="collectionUrl"
											placeholder="Enter collection URL"
											type="text"
											onChange={handleCollectionURLInputUpdate}
											value={collectionURLInput}
										/> */}
										<Button
											additionalClasses="h-12 w-20"
											variant="primary"
											disabled={
												!isValidCollectionURL || isFetchingCollectionURL
											}
											onClick={() => submitCollectionURL()}
										>
											{isFetchingCollectionURL ? (
												<div className="animate-spin">
													<LoadingIcon />
												</div>
											) : (
												'Add'
											)}
										</Button>
									</div>
								</div>
								<Button
									leftIcon={<PlusIcon />}
									size="medium"
									variant="tertiary"
									additionalClasses={clsx(
										errorFields.nfts ? 'text-red dark:text-red' : '',
									)}
									onClick={() => setAddNFTsManually(true)}
								>
									Add NFTs Manually
								</Button>
							</>
						) : (
							<ContentTile
								title={formData.name}
								description={getCollectionDescriptionString(formData)}
								image={formData.imageUrl}
								isVerified={true}
								showCaret={false}
								showBorder={false}
								styledBackground={true}
								variant="collection"
								noImageFallback={true}
							/>
						)}
						{errorFields.nfts && (
							<TypographyParagraph
								size="xSmall"
								additionalClasses={clsx('mt-2', 'text-red dark:text-red')}
							>
								Click the above button to add NFTs
							</TypographyParagraph>
						)}
					</div>
					<div className="flex flex-col gap-y-2">
						{attestationState === 'editing' &&
							Object.keys(marketplaceCollectionsObject).length > 0 &&
							Object.keys(marketplaceCollectionsObject).map((key, index) => {
								const marketplaceEntry = marketplaceCollectionsObject[key]
								const entry = marketplaceEntry.collection
								const entryDescription = entry
									? `${entry.chains.map((chain: string) => capitalizeFirstLetter(chain).toString())} - ${entry.distinct_nft_count} item${entry.distinct_nft_count > 1 ? 's' : ''}`
									: ''

								return (
									<div key={key}>
										<ContentTile
											title={entry && entry.name ? entry.name : ''}
											collectionType={'Marketplace Collection'}
											description={entryDescription}
											variant="collectionEntry"
											showCaret={false}
											showBorder={false}
											handleEdit={undefined}
											handleDelete={key =>
												deleteMarketplaceCollectionEntry(key)
											}
											collectionEntryKey={key}
											noImageFallback={true}
										/>
									</div>
								)
							})}
						{attestationState === 'editing' &&
							formData.nfts &&
							Array.isArray(formData.nfts) &&
							formData.nfts
								.filter(
									(entry: CollectionNFTInputType) =>
										entry.inputSelector &&
										!entry.inputSelector.includes('marketplace'),
								)
								.map((entry, index) => {
									const entryDescription = `${capitalizeFirstLetter(entry.chain)}${getEntryDescriptionString(entry)}`
									const key = entry.inputSelector
									return (
										<div key={entry.inputSelector}>
											<ContentTile
												title={lessTruncateStringInMiddle(entry.contract)}
												collectionType={getCollectionTypeName(entry.type)}
												description={entryDescription}
												variant="collectionEntry"
												showCaret={false}
												showBorder={false}
												handleEdit={key => editCollectionEntry(key)}
												handleDelete={key => deleteCollectionEntry(key)}
												collectionEntryKey={key}
												noImageFallback={true}
												collectionIndex={index}
											/>
										</div>
									)
								})}
					</div>
				</div>
			) : (
				<div className="flex flex-col md:w-3/5">
					<div className="mb-4 mt-8 h-10 w-[170px]">
						<SkeletonTheme
							baseColor={
								theme === 'light' || theme === undefined ? '#f5f5f5' : '#383838'
							}
							highlightColor={
								theme === 'light' || theme === undefined ? '#f8f8f8' : '#212121'
							}
						>
							<Skeleton count={1} className="h-10 w-[170px]" />
						</SkeletonTheme>
					</div>
				</div>
			)}
			<CollectionInputModal
				isOpen={addNFTsManually}
				formData={formData}
				errorFields={errorFields}
				existingInput={editingEntry ? editingEntry : undefined}
				handleInputDataChange={(val: CollectionNFTInputType) =>
					handleInputDataChange(val)
				}
				onClose={() => {
					setAddNFTsManually(false)
					if (editingEntry !== undefined) setEditingEntry(undefined)
				}}
			/>
			<Modal
				size="large"
				title="Marketplace Collection Data Retrieved"
				body={
					<div className="flex flex-col gap-y-2">
						<TypographyParagraph size="medium">
							We've automatically pulled marketplace data that can pre-fill
							parts of the form below.
						</TypographyParagraph>
						<TypographyParagraph size="medium" additionalClasses="mb-4">
							Choose to auto-fill this information, or enter it manually.
						</TypographyParagraph>
						<Button
							variant="primary"
							onClick={() => {
								updateFormWithMarketplaceCollectionData(pendingAutofillData)
								setIsAutofillModalOpen(false)
								setIsPendingAutofillData(undefined)
							}}
						>
							Auto-fill
						</Button>
						<Button
							variant="secondary"
							onClick={() => {
								setIsAutofillModalOpen(false)
								setIsPendingAutofillData(undefined)
							}}
						>
							Fill Manually
						</Button>
					</div>
				}
				isOpen={isAutofillModalOpen && pendingAutofillData !== undefined}
				onClose={() => {
					setIsPendingAutofillData(undefined)
					setIsAutofillModalOpen(false)
				}}
				showClose={true}
			/>
		</div>
	)
}

export const CollectionDetailsComponent = ({
	user,
	users,
	isImageUploading,
	setIsImageUploading,
	theme,
	formData,
	selected,
	setSelected,
	missingRequiredFields,
	errorFields,
	handleFormDataChange,
}: {
	user: UserProfile
	users: UserType[]
	isImageUploading: boolean
	setIsImageUploading: (val: boolean) => void
	theme: Theme | undefined
	formData: MetadataProperties | {}
	selected: UserType | undefined
	setSelected: (val: UserType) => void
	missingRequiredFields: {
		name: string
		slug: string
		required: boolean
		type: string
		placeholder: string
		placeHolder?: undefined
		hidden?: undefined
	}[]
	errorFields: { [key: string]: { [key: string]: string } }
	handleFormDataChange: (newVal: MetadataProperties | {}) => void
}) => {
	return (
		<div className="mt-8 flex flex-col gap-x-4 gap-y-2 md:flex-row">
			<div className="flex flex-col pr-[70px] md:w-2/5">
				<TypographyHeading
					size="medium"
					isBold={true}
					additionalClasses="text-textPrimaryLight dark:text-textPrimaryDark"
				>
					Collection details
				</TypographyHeading>
				<div className="flex flex-row items-center">
					<TypographyParagraph
						size="medium"
						additionalClasses="text-textSecondaryLight dark:text-textSecondaryDark"
					>
						Fill in the information about this Collection.
					</TypographyParagraph>
				</div>{' '}
			</div>
			<div className="flex flex-col md:w-3/5">
				<CollectionForm
					user={user}
					users={users}
					isImageUploading={isImageUploading}
					setIsImageUploading={setIsImageUploading}
					theme={theme}
					formData={formData}
					setFormData={newVal => handleFormDataChange(newVal)}
					selected={selected}
					setSelected={setSelected}
					requiredFields={missingRequiredFields}
					errorFields={errorFields}
				/>
			</div>
		</div>
	)
}
